import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash, faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import Question from '../../components/Question';
import { AnimatePresence, motion } from 'framer-motion';

// Import rune images
import perthoImage from '../../../img/runes/image-013.webp';
import jeraImage from '../../../img/runes/image-011.webp';
import eihwazImage from '../../../img/runes/image-012.webp';

const RunesTechniquesLesson = ({ 
  level = 'beginner', 
  onNextLesson, 
  markLessonComplete,
  currentQuestion = 0,
  onQuestionChange,
  currentLesson,
  completedQuestions = [],
  setCompletedQuestions
}) => {
  const sections = [
    {
      id: 'single',
      title: 'I. Single Rune Draw',
      content: `
        The single rune draw is the most basic and fundamental technique in runic divination. 
        It involves drawing one rune to gain insight into a specific question or situation.
        
        This method is excellent for beginners as it provides clear, focused guidance without overwhelming complexity. 
        The single rune can represent the core essence of the situation, the most important factor to consider, or the primary energy at work.
        
        When using this technique, focus your question clearly and draw the rune with intention. 
        The position of the rune (upright or reversed) can add additional layers of meaning to your interpretation.
      `,
      imageUrl: perthoImage,
      questions: {
        beginner: {
          question: "What is the most basic runic divination technique?",
          options: ["Three rune spread", "Single rune draw", "Celtic cross", "Tree of life"],
          correctAnswer: "Single rune draw",
        },
        intermediate: {
          question: "What can the single rune represent?",
          options: [
            "The core essence of the situation",
            "Only the past",
            "Only the future",
            "Only negative outcomes"
          ],
          correctAnswer: "The core essence of the situation",
        },
      },
    },
    {
      id: 'three',
      title: 'II. Three Rune Spread',
      content: `
        The three rune spread is one of the most popular and versatile techniques in runic divination. 
        It provides a more comprehensive view than a single rune while remaining manageable for interpretation.
        
        Common three-rune layouts include Past-Present-Future, Mind-Body-Spirit, and Situation-Action-Outcome. 
        Each position has a specific meaning that helps frame the interpretation of the rune in that position.
        
        This spread allows for more nuanced readings as the runes can interact with each other, 
        creating a narrative that provides deeper insight into the question or situation.
      `,
      imageUrl: jeraImage,
      questions: {
        beginner: {
          question: "What is a common three-rune layout?",
          options: ["Past-Present-Future", "North-South-East", "Fire-Water-Earth", "Sun-Moon-Stars"],
          correctAnswer: "Past-Present-Future",
        },
        intermediate: {
          question: "What advantage does the three-rune spread have over single rune?",
          options: [
            "More comprehensive view while remaining manageable",
            "It's faster to perform",
            "It requires less concentration",
            "It's more accurate"
          ],
          correctAnswer: "More comprehensive view while remaining manageable",
        },
      },
    },
    {
      id: 'casting',
      title: 'III. Rune Casting',
      content: `
        Rune casting involves throwing or scattering the runes onto a surface and interpreting the pattern they create. 
        This is one of the most traditional methods, mentioned in historical sources and archaeological evidence.
        
        The position of the runes relative to each other, their orientation, and their distance from the center all contribute to the interpretation. 
        Runes that land close together may have a stronger connection or influence on each other.
        
        This method requires practice and intuition, as the interpretation relies heavily on the reader's ability to see patterns and connections 
        between the runes and their positions.
      `,
      imageUrl: eihwazImage,
      questions: {
        beginner: {
          question: "What does rune casting involve?",
          options: ["Throwing or scattering runes", "Drawing runes one by one", "Reading runes in order", "Memorizing rune meanings"],
          correctAnswer: "Throwing or scattering runes",
        },
        intermediate: {
          question: "What factors contribute to interpretation in rune casting?",
          options: [
            "Position, orientation, and distance from center",
            "Only the rune meanings",
            "Only the time of day",
            "Only the question asked"
          ],
          correctAnswer: "Position, orientation, and distance from center",
        },
      },
    },
    {
      id: 'interpretation',
      title: 'IV. Interpretation Principles',
      content: `
        Interpretation in runic divination involves more than just knowing the individual meanings of runes. 
        It requires understanding how runes interact with each other and with the context of the question.
        
        Consider the position of each rune in the spread, its relationship to other runes, and how it relates to your specific question. 
        The same rune can have different meanings depending on the context and the other runes present.
        
        Trust your intuition while also grounding your interpretation in the traditional meanings of the runes. 
        The best interpretations often combine knowledge of runic tradition with personal insight and understanding.
      `,
      imageUrl: perthoImage,
      questions: {
        beginner: {
          question: "What does interpretation require beyond knowing rune meanings?",
          options: ["Understanding how runes interact", "Only memorization", "Only intuition", "Only historical knowledge"],
          correctAnswer: "Understanding how runes interact",
        },
        intermediate: {
          question: "What should you consider when interpreting runes?",
          options: [
            "Position, relationships, and context",
            "Only the individual rune meanings",
            "Only your personal feelings",
            "Only the time of day"
          ],
          correctAnswer: "Position, relationships, and context",
        },
      },
    },
    {
      id: 'practice',
      title: 'V. Developing Your Practice',
      content: `
        Developing skill in runic divination takes time and practice. Start with simple techniques and gradually work your way up to more complex spreads.
        
        Keep a journal of your readings, noting the question, the runes drawn, your interpretation, and how things actually unfolded. 
        This helps you learn from experience and develop your own understanding of how the runes work for you.
        
        Don't be discouraged by initial difficulties. Like any skill, runic divination improves with practice and patience. 
        Trust your growing intuition while continuing to study and learn about the runic tradition.
      `,
      imageUrl: jeraImage,
      questions: {
        beginner: {
          question: "What should you do to develop your runic practice?",
          options: ["Start with simple techniques", "Begin with complex spreads", "Memorize everything first", "Only read books"],
          correctAnswer: "Start with simple techniques",
        },
        intermediate: {
          question: "What should you keep to help develop your practice?",
          options: [
            "A journal of your readings",
            "Only expensive rune sets",
            "Only historical texts",
            "Only other people's interpretations"
          ],
          correctAnswer: "A journal of your readings",
        },
      },
    },
  ];

  const [selectedOption, setSelectedOption] = useState('');
  const [imageVisible, setImageVisible] = useState(true);
  const [imageSize, setImageSize] = useState('smallimage');
  const [allQuestionsAnsweredCorrectly, setAllQuestionsAnsweredCorrectly] = useState(false);

  useEffect(() => {
    if (allQuestionsAnsweredCorrectly) {
      markLessonComplete();
      onNextLesson();
    }
  }, [allQuestionsAnsweredCorrectly, markLessonComplete, onNextLesson]);

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      const newCompletedQuestions = [...completedQuestions];
      newCompletedQuestions[currentQuestion] = true;
      setCompletedQuestions(newCompletedQuestions);
      
      if (currentQuestion + 1 < sections.length) {
        onQuestionChange(currentQuestion + 1);
      } else {
        setAllQuestionsAnsweredCorrectly(true);
      }
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  const toggleImageSize = () => {
    setImageSize(imageSize === 'smallimage' ? 'largeimage' : 'smallimage');
  };

  const getImageStyle = () => {
    const baseStyle = {
      height: 'auto',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.3s ease-in-out'
    };

    if (imageSize === 'smallimage') {
      return {
        ...baseStyle,
        maxWidth: '150px',
        width: '150px'
      };
    } else {
      return {
        ...baseStyle,
        maxWidth: '300px',
        width: '300px'
      };
    }
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  return (
    <div>
      <Helmet>
        <title>Basic Divination Techniques - {level.charAt(0).toUpperCase() + level.slice(1)} Lesson</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {imageVisible && sections[currentQuestion]?.imageUrl && (
            <div className="has-text-centered mb-4">
              <img
                src={sections[currentQuestion].imageUrl}
                alt={`Divination Techniques - ${sections[currentQuestion].title}`}
                style={getImageStyle()}
              />
              <div className="icon-buttons mt-2">
                <FontAwesomeIcon
                  icon={faEyeSlash}
                  className="icon-button has-text-warning mr-3"
                  onClick={toggleImageVisibility}
                  style={{ cursor: 'pointer' }}
                />
                <FontAwesomeIcon
                  icon={faExpandArrowsAlt}
                  className="icon-button has-text-warning"
                  onClick={toggleImageSize}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
          )}

          <section className="section has-text-centered" id={sections[currentQuestion]?.id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">{sections[currentQuestion]?.title}</h2>
                  <p className="has-text-white">{sections[currentQuestion]?.content}</p>
                </div>
              </div>
            </div>
          </section>

          {sections[currentQuestion]?.questions && (
            <section className="section has-text-centered">
              <div className="container content">
                <div className="columns">
                  <div className="column">
                    <Question
                      question={sections[currentQuestion].questions[level].question}
                      options={sections[currentQuestion].questions[level].options}
                      correctAnswer={sections[currentQuestion].questions[level].correctAnswer}
                      onAnswered={handleAnswer}
                    />
                  </div>
                </div>
              </div>
            </section>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default RunesTechniquesLesson; 